// Client-type primitives (QP-184, lifted in QL-36).
//
// `client.type` is a plain varchar with three legal values. Before QL-36 the
// list was written out in four places (the flow settings tab, flow-crud's
// validator, and both goal-type surfaces were about to add a fifth and sixth).
// One list here, so adding a client type is a data question rather than a
// grep-and-hope.

export const ALL_CLIENT_TYPES = ["Person", "Company", "Group"] as const;

export type ClientType = (typeof ALL_CLIENT_TYPES)[number];

/**
 * Shared "may this client type use this thing" gate, unchanged from QP-184.
 * Null or an empty list means no restriction, and a *missing* client type is
 * allowed through, so a row that has not been classified yet is not locked out.
 *
 * An unrecognised type is NOT allowed through: it fails the `includes` check
 * like any other non-member. That is deliberate — a restricted list names the
 * types that may pass, so anything else is denied.
 *
 * Both named wrappers (`clientTypeAllowedForFlow`, `clientTypeAllowedForGoalType`)
 * call this so the two gates cannot drift apart.
 */
export function clientTypeAllowed(
  allowedClientTypes: string[] | null | undefined,
  clientType: string | null | undefined,
): boolean {
  if (!allowedClientTypes || allowedClientTypes.length === 0) return true;
  if (!clientType) return true;
  return allowedClientTypes.includes(clientType);
}

/**
 * Collapse a selection to the stored form: "all three selected" and "none
 * selected" both mean unrestricted, and both persist as null. Keeps one
 * canonical representation in the column so reads never have to ask whether
 * `["Person","Company","Group"]` and `null` are the same thing.
 */
export function normalizeAllowedClientTypes(types: string[] | null | undefined): string[] | null {
  if (!types || types.length === 0) return null;
  const known = ALL_CLIENT_TYPES.filter((ct) => types.includes(ct));
  // All three and none both mean unrestricted. A list of only unknown values
  // lands in the second case, so it stores as null rather than an empty array.
  return known.length === 0 || known.length === ALL_CLIENT_TYPES.length ? null : [...known];
}

/** The values in `types` that are not legal client types. Empty = all valid. */
export function invalidClientTypes(types: string[]): string[] {
  return types.filter((t) => !(ALL_CLIENT_TYPES as readonly string[]).includes(t));
}
